<template>
    <div class='participant-container' v-loading.fullscreen="isLoading">
      <el-drawer size="100%" v-model="isParticipant" title="参与人员" direction="btt" :before-close="handleClose"
        :with-header="false">
        <h1 class="tit">参与人员</h1>
        <el-icon class="close-btn" @click="handleClose" size="40px" color="#f00">
          <Close />
        </el-icon>
  
        <!-- 会议code查询区域（核心新增） -->
        <div class="query-container">
          <el-input 
            v-model="codeInput" 
            placeholder="请输入会议code查询签到人员" 
            style="width: 300px; margin-right: 20px;"
            @keyup.enter="handleQueryCheckIn"
          />
          <el-button type="primary" @click="handleQueryCheckIn">查询签到人员</el-button>
          <el-button type="default" @click="resetQuery" style="margin-left: 10px;">重置查询</el-button>
        </div>
  
        <!-- 原有数据操作按钮 -->
        <el-button class="imp-bt" color="#b79900" @click="downTemp">
          下载模板
        </el-button>
        <el-button class="imp-bt" color="#b79900">
          导入数据
          <input class="inp-xlsx" type="file" accept=".xls,.xlsx" @change="importData" />
        </el-button>
        <el-button class="imp-bt" color="#b79900" @click="deleteTab" v-if="!isQueryFromApi">
          删除数据
        </el-button>
  
        <!-- 数据表格（适配接口+导入数据） -->
        <el-table :data="tabDataS" style="width: 90vw;margin: 20px 2vw 0px 2vw;" height="80vh">
          <el-table-column type="index" label="序号" width="auto" min-width="10%" align="center" />
          <el-table-column prop="name" label="姓名" width="auto" min-width="20%" align="center" />
          <el-table-column prop="userId" label="用户ID" width="auto" min-width="15%" align="center" />
          <el-table-column prop="symbol" label="用户代号" width="auto" min-width="15%" align="center" />
          <el-table-column prop="code" label="会议编码" width="auto" min-width="15%" align="center" />
          <el-table-column prop="createdAt" label="签到/导入时间" width="auto" min-width="25%" align="center" />
        </el-table>
      </el-drawer>
    </div>
  </template>
  
  <script setup>
  import { ref, watch } from 'vue';
  import { ElMessage, ElMessageBox } from 'element-plus';
  import * as XLSX from "xlsx";
  import { saveAs } from 'file-saver';
//   import meetingApi from '@/api/meeting.js'; // 导入会议接口
  
  // props定义
  let props = defineProps({
    isParticipant: Boolean, // 抽屉显示状态
  });
  
  // 响应式变量
  let isParticipant = ref(false);
  let tabData = ref([]); // 原始数据（导入/接口查询）
  let tabDataS = ref([]); // 表格展示数据
  let isLoading = ref(false);
  let codeInput = ref(''); // 会议code输入框
  let isQueryFromApi = ref(false); // 是否是接口查询数据（控制删除按钮）
  let emits = defineEmits(["close", "getNameList"]);
  
  // 监听抽屉状态，初始化数据
  watch(
    () => props.isParticipant,
    (val) => {
      isParticipant.value = val;
      if (val) {
        // 初始化：清空所有状态
        tabData.value = [];
        tabDataS.value = [];
        codeInput.value = '';
        isQueryFromApi.value = false;
      }
    }
  );
  
  // 关闭对话框
  const handleClose = () => {
    emits("close");
  };
  
  // 下载模板（保留原有功能）
  const downTemp = () => {
    let fileName = "人员模板.xlsx";
    let fileUrl = "./template/";
    saveAs(fileUrl + fileName, fileName).catch(() => {
      ElMessage.error('模板下载失败，请检查文件路径');
    });
  };
  
  // 导入数据（保留原有功能，适配表格字段）
  const importData = (e) => {
    isLoading.value = true;
    let file = e.target.files[0];
    if (!file) return;
  
    let fileReader = new FileReader();
    fileReader.onload = (event) => {
      try {
        let result = event.target.result;
        let workBook = XLSX.read(result, { type: "binary" });
        let jsonData = XLSX.utils.sheet_to_json(
          workBook.Sheets[workBook.SheetNames[0]]
        );
  
        // 处理导入数据格式（适配表格字段）
        tabData.value = jsonData.map((item) => ({
          name: item.姓名 || '无',
          userId: item.用户ID || '无',
          symbol: item.用户代号 || '无',
          createdAt: new Date().toLocaleString(), // 导入时间
        }));
  
        tabDataS.value = [...tabData.value];
        isQueryFromApi.value = false; // 标记为导入数据
        emits("getNameList", tabData.value);
        ElMessage.success('数据导入成功');
      } catch (error) {
        ElMessage.error('数据导入失败，请检查文件格式');
        console.error(error);
      } finally {
        isLoading.value = false;
      }
    };
    fileReader.readAsBinaryString(file);
    document.getElementsByClassName("inp-xlsx")[0].value = "";
  };
  
  // 删除数据（仅对导入数据有效）
  const deleteTab = () => {
    ElMessageBox.confirm(
      '确认删除当前导入的数据?',
      { confirmButtonText: '确认', cancelButtonText: '取消', type: 'warning' }
    ).then(() => {
      tabData.value = [];
      tabDataS.value = [];
      emits("getNameList", []);
      ElMessage.success('删除成功');
    }).catch(() => {
      ElMessage.info('取消删除');
    });
  };
  
  // 核心新增：查询签到人员（调用getMeetingCheckInList接口）
  const handleQueryCheckIn = async () => {
    if (!codeInput.value.trim()) {
      ElMessage.warning('请输入会议code');
      return;
    }
  
    isLoading.value = true;
    try {
      // 调用接口查询签到人员（默认查询全部，size设为10000）
      const res = await meetingApi.getMeetingCheckInList({
        current: 1,
        size: 10000,
        code: codeInput.value.trim()
      });
  
      if (res.code === 0 && res.data?.records) {
        const checkInData = res.data.records;
        if (checkInData.length === 0) {
          ElMessage.info('该会议暂无签到人员');
          tabData.value = [];
          tabDataS.value = [];
          isQueryFromApi.value = true;
          return;
        }
  
        // 处理接口数据，适配表格字段
        tabData.value = checkInData.map(item => ({
          name: item.name || '无',
          userId: item.userId || '无',
          symbol: item.symbol || '无',
          department: '无', // 签到接口无部门字段
          createdAt: item.createdAt ? new Date(item.createdAt).toLocaleString() : '无',
          age: '无' // 签到接口无性别字段
        }));
  
        tabDataS.value = tabData.value;
        isQueryFromApi.value = true; // 标记为接口查询数据（隐藏删除按钮）
        emits("getNameList", tabData.value);
        ElMessage.success(`查询成功，共${checkInData.length}名签到人员`);
      } else {
        ElMessage.error(res.msg || '查询失败，请检查会议code是否正确');
      }
    } catch (error) {
      ElMessage.error('网络异常，查询失败');
      console.error('签到人员查询失败:', error);
    } finally {
      isLoading.value = false;
    }
  };
  
  // 重置查询（清空接口查询数据）
  const resetQuery = () => {
    codeInput.value = '';
    tabData.value = [];
    tabDataS.value = [];
    isQueryFromApi.value = false;
    ElMessage.info('查询已重置');
  };
  </script>
  
  <style lang="scss" scoped>
  .participant-container {
    height: 100%;
    width: 100%;
    text-align: center;
    padding-top: 60px; /* 预留标题空间 */
  
    .tit {
      position: absolute;
      left: 40px;
      top: 20px;
      font-size: 24px;
      font-weight: 700;
      color: #333;
    }
  
    .close-btn {
      cursor: pointer;
      position: absolute;
      right: 40px;
      top: 20px;
    }
  
    // 查询区域样式
    .query-container {
      margin: 20px 0;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  
    .imp-bt {
      color: #fff;
      margin: 10px 20px;
      padding: 12px 24px;
      font-weight: 700;
      font-size: 16px;
      position: relative;
  
      input {
        position: absolute;
        left: 0;
        top: 0;
        z-index: 10;
        width: 100%;
        height: 100%;
        opacity: 0;
        cursor: pointer;
      }
    }
  }
  </style>